import * as React from 'react';
import { cn } from '../lib/cn';

export interface UniversalAppLayoutProps {
  /** Primary navigation — typically `<UniversalTopNav />`. */
  topNav: React.ReactNode;
  /** Optional second-row in-section navigation — typically `<UniversalSubNav />`. */
  subNav?: React.ReactNode;
  /** Optional footer — typically `<UniversalAppFooter />`. Pinned to the bottom of the viewport. */
  footer?: React.ReactNode;
  /** Page content slot — pass `<Outlet />` from React Router. */
  children: React.ReactNode;
  /** Override the main content background. Defaults to the theme's background. */
  contentClassName?: string;
}

/**
 * UniversalAppLayout — outer shell for every authenticated app.
 *
 * Layout (top → bottom, exactly 100vh):
 *   [ topNav   (h-16, sticky, shadow)            ]
 *   [ subNav   (h-12, sticky, optional)          ]
 *   [ main     (flex-1, min-h-0, scrollable)     ]
 *   [ footer   (mt-auto, fixed-height, optional) ]
 *
 * The container is locked to `h-screen max-h-screen` with `overflow-hidden`
 * so the chrome never exceeds the viewport. The `<main>` element MUST carry
 * `min-h-0` — without it, flex's default `min-height: auto` lets main grow
 * to fit its intrinsic content, defeating `flex-1` and pushing the footer
 * below the viewport. The footer's own `mt-auto shrink-0` is belt-and-braces;
 * the structural pin comes from the wrapper height + main shrinking correctly.
 */
export function UniversalAppLayout({
  topNav,
  subNav,
  footer,
  children,
  contentClassName,
}: UniversalAppLayoutProps) {
  return (
    <div className="flex h-screen max-h-screen w-full flex-col overflow-hidden bg-background">
      {topNav}
      {subNav}
      <main
        className={cn(
          'flex-1 min-h-0 overflow-y-scroll px-6 py-6',
          contentClassName
        )}
        // scrollbar-gutter: stable reserves the scrollbar slot whether or not
        // the page is tall enough to scroll. Without this, navigating from a
        // short page (no scrollbar) to a long page (scrollbar appears) shifts
        // all content left by ~15 px — which reads as the page "jumping" on
        // every click. `overflow-y-scroll` (vs `auto`) belt-and-braces this
        // for browsers that don't support scrollbar-gutter yet.
        style={{ scrollbarGutter: 'stable' }}
      >
        {children}
      </main>
      {footer}
    </div>
  );
}
